import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Stack, useTheme } from '@mui/material'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { SerializedError } from '@reduxjs/toolkit'

import OrderCardList from 'components/order-card-list/order-card-list.component'
import TabPanel from 'components/tabs/tab-panel/tab-panel.component'
import BasicTabsWithSearch from 'components/tabs/basic-tabs-with-search/basic-tabs-with-search.component'
import AddButton from 'components/add-button/add-button.component'
import PrevNextPagination from 'components/prev-next-pagination/prev-next-pagination.component'
import FileUpload from 'components/file-upload/file-upload.component'

import { RootState } from 'store'
import { Order } from 'store/orders/orders.types'
import {
  clearSelectedOrder,
  setOrderType,
  setSelectedCompany,
  setSelectedOrder,
  setIsSelectedOrderLoading,
} from 'store/orders/orders.slice'
import {
  useLazyGetOrderByIdQuery,
  useLazyListNewOrdersQuery,
  useLazyListActiveOrdersQuery,
  useLazyListArchivedOrdersQuery,
} from 'store/orders/orders.api'
import { Company } from 'store/company/company.types'
import { useLazyGetCompanyByIdQuery } from 'store/company/company.api'
import { Page } from 'store/common.types'
import {
  useGetPendingLoadConfirmationsQuery,
  useUploadLoadConfirmationMutation,
} from 'store/task-automation/task-automation.api'
import { PendingLoadConfirmationDocument } from 'store/task-automation/task-automation.types'
import { getRole, getUserId } from 'utils/common'
import { tabs } from './order-selection.constant'
import CreateManualOrderDialog from 'pages/order/create-manual-order/create-manual-order-dialog'

type OptimisticUpload = {
  id: number
  fileName: string
}

const stripFileExtension = (name: string) => name.replace(/\.[^/.]+$/, '')

const comparableFileName = (name: string) =>
  name.trim().toLowerCase().replace(/^failed_/, '')

const fileNamesMatch = (uploadedName: string, pendingName: string) => {
  const uploaded = comparableFileName(uploadedName)
  const pending = comparableFileName(pendingName)
  if (!uploaded || !pending) return false
  if (uploaded === pending) return true
  return stripFileExtension(uploaded) === stripFileExtension(pending)
}

const pendingDocToOrder = (doc: PendingLoadConfirmationDocument, index: number): Order => {
  const nameWithoutExtension = stripFileExtension(doc.file_name)

  return {
    orderId: -1000 - index,
    userOrderId: '',
    status: 'NEW',
    loadNumber: '',
    companyId: '',
    customerId: '',
    customerName: nameWithoutExtension,
    notificationEmail: '',
    billingEmail: '',
    baseRate: 0,
    detentionCharges: 0,
    layoverCharges: 0,
    fuelSurcharge: 0,
    otherCharges: 0,
    notes: '',
    accountPayableEmail: '',
    currency: 'CAD',
    orderEvents: [],
    isTempLcDoc: true,
    fileName: nameWithoutExtension,
    randomUuid: doc.random_uuid,
    s3Key: doc.s3_key,
  }
}

const optimisticUploadToOrder = (upload: OptimisticUpload): Order => {
  const nameWithoutExtension = stripFileExtension(upload.fileName)

  return {
    orderId: upload.id,
    userOrderId: '',
    status: 'NEW',
    loadNumber: '',
    companyId: '',
    customerId: '',
    customerName: nameWithoutExtension,
    notificationEmail: '',
    billingEmail: '',
    baseRate: 0,
    detentionCharges: 0,
    layoverCharges: 0,
    fuelSurcharge: 0,
    otherCharges: 0,
    notes: '',
    accountPayableEmail: '',
    currency: 'CAD',
    orderEvents: [],
    isTempLcDoc: true,
    isOptimisticUpload: true,
    fileName: nameWithoutExtension,
  }
}

const uploadErrorMessage = (err: unknown) => {
  const error = err as FetchBaseQueryError | SerializedError
  if ('data' in (error as FetchBaseQueryError)) {
    const fetchError = error as FetchBaseQueryError
    return typeof fetchError.data === 'string'
      ? fetchError.data
      : ((fetchError.data as { detail?: string })?.detail ?? 'Server error')
  }
  return 'Something went wrong. Please try again later!'
}

export interface OrderSelectionProps {
  newOrders: Order[]
  activeOrders: Order[]
  selectedOrder: Order
  selectedCompany: Company
  refetchNewOrders: () => void
  refetchActiveOrders: () => void
}

const OrderSelection = () => {
  const dispatch = useDispatch()
  const store = useStore<RootState>()
  const [tabsValue, setTabsValue] = useState(0)
  const [page, setPage] = useState(1)
  const [isOrderSelectedManually, setIsOrderSelectedManually] = useState(false)
  const [search, setSearch] = useState('')
  const [repeatOrderOpen, setRepeatOrderOpen] = useState(false)
  const [repeatOrderTemplate, setRepeatOrderTemplate] = useState<Order | null>(null)
  const [repeatOrderCompany, setRepeatOrderCompany] = useState<Company | null>(null)
  const [loadConfirmOpen, setLoadConfirmOpen] = useState(false)
  const [manualCreateOpen, setManualCreateOpen] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [optimisticUploads, setOptimisticUploads] = useState<OptimisticUpload[]>([])
  const optimisticIdRef = useRef(-200000)
  const userId = getUserId()
  const role = getRole()
  const isDispatch = role === 'DISPATCH'
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  const baseTabs = useMemo(
    () => (isDispatch ? tabs.filter((tab) => tab.id !== 'new') : tabs),
    [isDispatch]
  )
  const selectedTabId = baseTabs[tabsValue]?.id ?? 'new'

  const selectedOrder = useSelector(
    (state: RootState) => state.orders.selectedOrder
  )

  const [getOrderById] = useLazyGetOrderByIdQuery()
  const [getCompanyById] = useLazyGetCompanyByIdQuery()
  const [listNewOrders, { data: newOrders }] = useLazyListNewOrdersQuery()
  const [listActiveOrders, { data: activeOrders }] =
    useLazyListActiveOrdersQuery()
  const [listArchiveOrders, { data: archivedOrders }] =
    useLazyListArchivedOrdersQuery()
  const { data: pendingLoadConfirmations, refetch: refetchPendingLoadConfirmations } =
    useGetPendingLoadConfirmationsQuery(
      { user_id: userId },
      {
        skip: !userId || selectedTabId !== 'new',
        pollingInterval: 5000,
      }
    )
  const [uploadLC, { isLoading: uploadLCLoading }] = useUploadLoadConfirmationMutation()

  const unmatchedOptimisticUploads = useMemo(() => {
    const pendingDocs = pendingLoadConfirmations?.pending_documents || []
    return optimisticUploads.filter(
      (upload) =>
        !pendingDocs.some((doc) => fileNamesMatch(upload.fileName, doc.file_name))
    )
  }, [optimisticUploads, pendingLoadConfirmations?.pending_documents])

  const visibleTabs = useMemo(() => {
    const pendingCount = pendingLoadConfirmations?.pending_documents?.length ?? 0
    const newCount =
      (newOrders?.page?.totalElements ?? newOrders?.orders?.length ?? 0) +
      pendingCount +
      unmatchedOptimisticUploads.length
    const activeCount =
      activeOrders?.page?.totalElements ?? activeOrders?.orders?.length ?? 0
    const archiveCount =
      archivedOrders?.page?.totalElements ?? archivedOrders?.orders?.length ?? 0

    return baseTabs.map((tab) => {
      if (tab.id === 'new') {
        return { ...tab, label: `New (${newCount})` }
      }
      if (tab.id === 'assigned') {
        return { ...tab, label: `Assigned (${activeCount})` }
      }
      if (tab.id === 'delivered') {
        return { ...tab, label: `Completed (${archiveCount})` }
      }
      return tab
    })
  }, [
    baseTabs,
    newOrders?.page?.totalElements,
    newOrders?.orders?.length,
    activeOrders?.page?.totalElements,
    activeOrders?.orders?.length,
    archivedOrders?.page?.totalElements,
    archivedOrders?.orders?.length,
    pendingLoadConfirmations?.pending_documents?.length,
    unmatchedOptimisticUploads.length,
  ])

  const handlePagination = async (
    _event: ChangeEvent<unknown>,
    page: number
  ) => {
    setPage(page)
    try {
      if (selectedTabId === 'new') {
        await listNewOrders({ page: page - 1, size: 10 }).unwrap()
      } else if (selectedTabId === 'assigned') {
        await listActiveOrders({ page: page - 1, size: 10 }).unwrap()
      } else if (selectedTabId === 'delivered') {
        await listArchiveOrders({ page: page - 1, size: 10 }).unwrap()
      }
    } catch (error) {
      console.error('Failed to fetch orders: ', error)
    }
  }

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabsValue(newValue)
    setPage(1)
    setSearch('')

    const newTabId = visibleTabs[newValue]?.id

    switch (newTabId) {
      case 'new':
        dispatch(setOrderType('NEW'))
        return
      case 'assigned':
        dispatch(setOrderType('ASSIGNED'))
        return
      case 'delivered':
        dispatch(setOrderType('DELIVERED'))
        return
      default:
        dispatch(setOrderType('NEW'))
        return
    }
  }

  const fetchNewOrders = useCallback(async () => {
    try {
      await listNewOrders({ page: page - 1, size: 10 }).unwrap()
    } catch (error) {
      console.error('Failed to fetch orders: ', error)
    }
  }, [listNewOrders, page])

  const fetchActiveOrders = useCallback(async () => {
    try {
      await listActiveOrders({ page: page - 1, size: 10 }).unwrap()
    } catch (error) {
      console.error('Failed to fetch orders: ', error)
    }
  }, [listActiveOrders, page])

  const handleRepeatOrder = useCallback(
    async (order: Order) => {
      try {
        const { data } = await getOrderById({ orderId: order.orderId })
        if (!data) return

        let company: Company | null = null
        if (data.companyId) {
          const { data: companyData } = await getCompanyById({ id: data.companyId })
          company = companyData ?? null
        }

        setRepeatOrderTemplate(data)
        setRepeatOrderCompany(company)
        setRepeatOrderOpen(true)
      } catch (error) {
        console.error('Failed to load order for repeat:', error)
      }
    },
    [getOrderById, getCompanyById]
  )

  const handleCloseRepeatOrder = useCallback(() => {
    setRepeatOrderOpen(false)
    setRepeatOrderTemplate(null)
    setRepeatOrderCompany(null)
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const shouldOpenUpload = params.get('uploadLC') === '1'

    if (shouldOpenUpload) {
      setLoadConfirmOpen(true)
      setUploadError('')
      navigate('/order', { replace: true })
    }
  }, [location.search, navigate])

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    optimisticIdRef.current -= 1
    const optimisticUpload: OptimisticUpload = {
      id: optimisticIdRef.current,
      fileName: file.name,
    }

    setLoadConfirmOpen(false)
    setUploadError('')
    setSearch('')
    setPage(1)
    setOptimisticUploads((current) => [optimisticUpload, ...current])

    const newTabIndex = baseTabs.findIndex((tab) => tab.id === 'new')
    if (newTabIndex >= 0) {
      setTabsValue(newTabIndex)
      dispatch(setOrderType('NEW'))
    }

    setIsOrderSelectedManually(true)
    dispatch(setIsSelectedOrderLoading(false))
    dispatch(setSelectedOrder({ selectedOrder: optimisticUploadToOrder(optimisticUpload) }))

    void uploadLC({ file })
      .unwrap()
      .then(() => {
        void refetchPendingLoadConfirmations().catch(() => undefined)
      })
      .catch((err: unknown) => {
        setOptimisticUploads((current) =>
          current.filter((upload) => upload.id !== optimisticUpload.id)
        )
        if (store.getState().orders.selectedOrder?.orderId === optimisticUpload.id) {
          dispatch(clearSelectedOrder())
          setIsOrderSelectedManually(false)
        }
        setUploadError(uploadErrorMessage(err))
        setLoadConfirmOpen(true)
      })
  }

  const fetchArchiveOrders = useCallback(async () => {
    try {
      await listArchiveOrders({ page: page-1, size: 10 }).unwrap()
    } catch (error) {
      console.error('Failed to fetch orders: ', error)
    }
  }, [listArchiveOrders, page])

  const fetchOrder = useCallback(
    async (orderId: number, listOrder?: Order) => {
      if (!orderId) {
        dispatch(clearSelectedOrder())
        return
      }

      // Highlight immediately; show skeleton until full details arrive
      if (listOrder) {
        dispatch(setSelectedOrder({ selectedOrder: listOrder }))
      }
      dispatch(setIsSelectedOrderLoading(true))

      try {
        const { data: ordersData, error } = await getOrderById({ orderId })

        if (store.getState().orders.selectedOrder?.orderId !== orderId) {
          return
        }

        // If 404 or no data, clear it
        if (error && 'status' in error && error.status === 404) {
          console.log('Order not found (404)')
          dispatch(clearSelectedOrder())
          return
        }

        if (!ordersData) {
          dispatch(clearSelectedOrder())
          return
        }

        dispatch(setSelectedOrder({ selectedOrder: ordersData }))

        const companyId = ordersData.companyId
        if (companyId) {
          const { data: companiesData } = await getCompanyById({ id: companyId })
          if (store.getState().orders.selectedOrder?.orderId !== orderId) {
            return
          }
          if (companiesData)
            dispatch(setSelectedCompany({ selectedCompany: companiesData }))
        }
      } catch (e) {
        console.error('Error fetching order:', e)
        if (store.getState().orders.selectedOrder?.orderId === orderId) {
          dispatch(clearSelectedOrder())
        }
      } finally {
        if (store.getState().orders.selectedOrder?.orderId === orderId) {
          dispatch(setIsSelectedOrderLoading(false))
        }
      }
    },
    [dispatch, getCompanyById, getOrderById, store]
  )

  const pagination: Page | undefined = useMemo(() => {
    switch (selectedTabId) {
      case 'new':
        return newOrders?.page
      case 'assigned':
        return activeOrders?.page
      case 'delivered':
        return archivedOrders?.page
      default:
        return {
          size: 0,
          totalElements: 0,
          totalPages: 0,
          number: 0,
        }
    }
  }, [selectedTabId, newOrders?.page, activeOrders?.page, archivedOrders?.page])

  const searchQuery = useMemo(
    () => search.trim().toLowerCase(),
    [search]
  )

  const filterOrdersByCustomerName = useCallback(
    (orders: Order[] | undefined) => {
      if (!orders) return []
      if (!searchQuery) return orders

      return orders.filter((order) =>
        (order.customerName || '').toLowerCase().includes(searchQuery) ||
        (order.userOrderId || '').toLowerCase().includes(searchQuery)
      )
    },
    [searchQuery]
  )

  const assignedOrders = useMemo(
    () => activeOrders?.orders || [],
    [activeOrders?.orders]
  )

  const combinedNewOrders = useMemo(() => {
    const baseOrders = newOrders?.orders || []
    const pendingDocs = pendingLoadConfirmations?.pending_documents || []
    const pendingAsOrders = pendingDocs.map((doc, index) => pendingDocToOrder(doc, index))
    const optimisticAsOrders = unmatchedOptimisticUploads.map(optimisticUploadToOrder)

    return [...optimisticAsOrders, ...pendingAsOrders, ...baseOrders]
  }, [
    newOrders?.orders,
    pendingLoadConfirmations?.pending_documents,
    unmatchedOptimisticUploads,
  ])

  useEffect(() => {
    if (isDispatch && selectedTabId === 'assigned') {
      dispatch(setOrderType('ASSIGNED'))
    }
  }, [dispatch, isDispatch, selectedTabId])

  useEffect(() => {
    if (selectedTabId === 'new') {
      fetchNewOrders()
    } else if (selectedTabId === 'assigned') {
      fetchActiveOrders()
    } else if (selectedTabId === 'delivered') {
      fetchArchiveOrders()
    }
  }, [fetchNewOrders, fetchActiveOrders, fetchArchiveOrders, selectedTabId])

  // Prefetch only inactive tabs for counts — never overwrite the active tab's page.
  useEffect(() => {
    if (selectedTabId !== 'new') {
      void listNewOrders({ page: 0, size: 10 })
    }
    if (selectedTabId !== 'assigned') {
      void listActiveOrders({ page: 0, size: 10 })
    }
    if (selectedTabId !== 'delivered') {
      void listArchiveOrders({ page: 0, size: 10 })
    }
  }, [selectedTabId, listNewOrders, listActiveOrders, listArchiveOrders])

  // Reset manual selection flag when switching tabs to enable auto-selection.
  // Keep the just-uploaded processing card selected when landing on New.
  useEffect(() => {
    const tabId = baseTabs[tabsValue]?.id
    if (tabId === 'new' && store.getState().orders.selectedOrder?.isOptimisticUpload) {
      return
    }
    setIsOrderSelectedManually(false)
  }, [tabsValue, baseTabs, store])

  // Clear selected order only when the current tab has zero orders (avoids blink:
  // we do not clear when switching to a tab that has orders; the delayed auto-select
  // will set the first order so we go straight from previous order to new tab's first order)
  useEffect(() => {
    if (!selectedOrder?.orderId) return

    const currentOrders =
      selectedTabId === 'new'
        ? combinedNewOrders
        : selectedTabId === 'assigned'
          ? assignedOrders
          : archivedOrders?.orders

    // Only run when we have data for the current tab (avoid clearing while loading)
    if (selectedTabId === 'assigned' && activeOrders === undefined) return
    if (selectedTabId === 'delivered' && archivedOrders === undefined) return

    if (!currentOrders?.length) {
      dispatch(clearSelectedOrder())
    }
  }, [
    tabsValue,
    selectedTabId,
    combinedNewOrders,
    assignedOrders,
    activeOrders,
    activeOrders?.orders,
    archivedOrders,
    archivedOrders?.orders,
    selectedOrder?.orderId,
    dispatch,
  ])

  // Drop the local filename card once the temp S3 list returns that same file.
  useEffect(() => {
    const pendingDocs = pendingLoadConfirmations?.pending_documents || []
    if (!optimisticUploads.length || !pendingDocs.length) return

    const matched = optimisticUploads.filter((upload) =>
      pendingDocs.some((doc) => fileNamesMatch(upload.fileName, doc.file_name))
    )
    if (!matched.length) return

    setOptimisticUploads((current) =>
      current.filter(
        (upload) =>
          !pendingDocs.some((doc) => fileNamesMatch(upload.fileName, doc.file_name))
      )
    )

    const selected = store.getState().orders.selectedOrder
    if (!selected?.isOptimisticUpload) return

    const selectedUpload = matched.find((upload) => upload.id === selected.orderId)
    if (!selectedUpload) return

    const docIndex = pendingDocs.findIndex((doc) =>
      fileNamesMatch(selectedUpload.fileName, doc.file_name)
    )
    if (docIndex < 0) return

    dispatch(
      setSelectedOrder({
        selectedOrder: pendingDocToOrder(pendingDocs[docIndex], docIndex),
      })
    )
    setIsOrderSelectedManually(true)
  }, [optimisticUploads, pendingLoadConfirmations?.pending_documents, dispatch, store])

  // Clear selected order if it's a processing card that no longer exists
  useEffect(() => {
    if (selectedOrder?.isOptimisticUpload) return

    if (selectedOrder?.isTempLcDoc && selectedTabId === 'new') {
      const pendingDocs = pendingLoadConfirmations?.pending_documents || []
      const selectedOrderExists = pendingDocs.some(
        (doc) =>
          doc.random_uuid === selectedOrder.randomUuid ||
          doc.file_name.replace(/\.[^/.]+$/, '') === selectedOrder.fileName
      )

      if (!selectedOrderExists) {
        dispatch(clearSelectedOrder())
        setIsOrderSelectedManually(false)
      }
    }
  }, [
    selectedOrder?.isOptimisticUpload,
    selectedOrder?.isTempLcDoc,
    selectedOrder?.randomUuid,
    selectedOrder?.fileName,
    pendingLoadConfirmations?.pending_documents,
    selectedTabId,
    dispatch,
  ])

  // Auto-select first order with short delay to avoid blinking when switching tabs (Trip page pattern)
  useEffect(() => {
    if (isOrderSelectedManually) return

    const timeoutId = window.setTimeout(() => {
      if (selectedTabId === 'new') {
        if (combinedNewOrders?.length > 0) {
          const firstOrder = combinedNewOrders[0]
          if (firstOrder.isTempLcDoc) {
            dispatch(setIsSelectedOrderLoading(false))
            dispatch(setSelectedOrder({ selectedOrder: firstOrder }))
          } else {
            fetchOrder(firstOrder.orderId, firstOrder)
          }
        }
      } else if (selectedTabId === 'assigned' && (assignedOrders.length ?? 0) > 0) {
        const firstOrder = assignedOrders?.[0]
        if (firstOrder?.orderId) fetchOrder(firstOrder.orderId, firstOrder)
      } else if (selectedTabId === 'delivered' && (archivedOrders?.orders?.length ?? 0) > 0) {
        const firstOrder = archivedOrders?.orders?.[0]
        if (firstOrder?.orderId) fetchOrder(firstOrder.orderId, firstOrder)
      }
    }, 0)

    return () => window.clearTimeout(timeoutId)
  }, [
    tabsValue,
    selectedTabId,
    newOrders,
    activeOrders,
    archivedOrders,
    combinedNewOrders,
    assignedOrders,
    fetchOrder,
    isOrderSelectedManually,
    dispatch,
  ])

  useEffect(() => {
    const refetchInterval = setInterval(() => {
      if (selectedTabId === 'new') {
        fetchNewOrders()
      } else if (selectedTabId === 'assigned') {
        fetchActiveOrders()
      } else if (selectedTabId === 'delivered') {
        fetchArchiveOrders()
      }
    }, 5000)

    return () => {
      clearInterval(refetchInterval)
    }
  }, [fetchActiveOrders, fetchArchiveOrders, fetchNewOrders, selectedTabId])

  return (
    <>
      <Stack
        justifyContent="space-between"
        sx={{
          height: '100%',
          minHeight: 0,
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <BasicTabsWithSearch
            tabs={visibleTabs}
            value={tabsValue}
            handleChange={handleChange}
            searchValue={search}
            searchPlaceholder="Search orders..."
            onSearchChange={(event) => setSearch(event.target.value)}
            sx={{ position: 'relative' }}
          >
            {visibleTabs.map((tab, index) => {
              if (tab.id === 'new') {
                return (
                  <TabPanel key={tab.id} value={tabsValue} index={index}>
                    <OrderCardList
                      orders={filterOrdersByCustomerName(combinedNewOrders)}
                      selectedOrder={selectedOrder}
                      setIsOrderSelectedManually={setIsOrderSelectedManually}
                      emptyMessage="No new orders"
                    />
                  </TabPanel>
                )
              }

              if (tab.id === 'assigned') {
                return (
                  <TabPanel key={tab.id} value={tabsValue} index={index}>
                    <OrderCardList
                      orders={filterOrdersByCustomerName(assignedOrders)}
                      selectedOrder={selectedOrder}
                      setIsOrderSelectedManually={setIsOrderSelectedManually}
                      emptyMessage="No assigned orders"
                    />
                  </TabPanel>
                )
              }

              if (tab.id === 'delivered') {
                return (
                  <TabPanel key={tab.id} value={tabsValue} index={index}>
                    <OrderCardList
                      orders={filterOrdersByCustomerName(archivedOrders?.orders)}
                      selectedOrder={selectedOrder}
                      setIsOrderSelectedManually={setIsOrderSelectedManually}
                      emptyMessage="No archive orders"
                      onRepeatOrder={handleRepeatOrder}
                    />
                  </TabPanel>
                )
              }

              return null
            })}
          </BasicTabsWithSearch>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            backgroundColor: theme.palette.common.white,
            borderTop: `1px solid ${theme.palette.divider}`,
            mt: 'auto',
            flexShrink: 0,
            pt: 0.5,
            pb: 1,
            px: 0.5,
            minHeight: 60,
          }}
        >
          {!isDispatch ? (
            <AddButton
              label="New Order"
              handleClick={() => {
                setUploadError('')
                setLoadConfirmOpen(true)
              }}
            />
          ) : (
            <Box />
          )}
          {!!pagination && pagination.totalPages > 0 && (
            <PrevNextPagination page={pagination} onChange={handlePagination} />
          )}
        </Box>
      </Stack>
      <FileUpload
        open={loadConfirmOpen}
        handleClose={() => {
          setLoadConfirmOpen(false)
          setUploadError('')
        }}
        onFileUpload={handleFileUpload}
        isLoading={uploadLCLoading}
        error={uploadError}
        showCreateManually
        onCreateManually={() => {
          setLoadConfirmOpen(false)
          setUploadError('')
          handleCloseRepeatOrder()
          setManualCreateOpen(true)
        }}
      />
      <CreateManualOrderDialog
        open={manualCreateOpen || repeatOrderOpen}
        onClose={() => {
          setManualCreateOpen(false)
          handleCloseRepeatOrder()
        }}
        templateOrder={repeatOrderTemplate}
        templateCompany={repeatOrderCompany}
        onCreated={() => {
          setManualCreateOpen(false)
          handleCloseRepeatOrder()
          void fetchNewOrders()
        }}
      />
    </>
  )
}

export default OrderSelection
